/**
 * Pure geometry math for the analytics evolution chart (no React, no DOM).
 *
 * Ported from the "Analytics — handoff a GROK.md" prototype's "Notas de
 * implementación": fixed padding, fixed height, 5 gridlines, ticks every
 * `ceil(n/9)` slots, point dots only up to 30 points, end-of-series label
 * offset of 9px. Kept here (rather than in `apps/center`) because it is
 * generic, testable, pure-TS logic — the only kind this monorepo's jest
 * setup can run (see `front/.claude/CLAUDE.md`). `apps/center`'s
 * `components/analytics/charts/evolutionChart.tsx` deep-imports this module.
 */

export const PADDING = { left: 52, right: 62, top: 18, bottom: 36 } as const;

export const CHART_HEIGHT = 300;

export const GRIDLINES = 5;

export const MAX_DOT_POINTS = 30;

export const END_LABEL_OFFSET = 9;

type Point = { x: number; y: number };

/** Rounds to 6 decimal places to keep generated numbers free of float dust. */
const round6 = (value: number): number => Math.round(value * 1e6) / 1e6;

const niceNumber = (value: number, ceil: boolean): number => {
  if (value <= 0) return 0;
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / 10 ** exponent;
  let niceFraction: number;
  if (ceil) {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  }
  return niceFraction * 10 ** exponent;
};

/**
 * Computes a clean y-axis scale: a "nice" max value and `GRIDLINES` evenly
 * spaced ticks from 0 to that max (inclusive), so gridline labels never show
 * ugly numbers like 83 or 1170.
 */
export const niceYScale = (
  maxValue: number,
): { max: number; ticks: number[] } => {
  const safeMax = Number.isFinite(maxValue) && maxValue > 0 ? maxValue : 0;
  if (safeMax === 0) {
    return { max: 0, ticks: Array.from({ length: GRIDLINES }, () => 0) };
  }
  const step = niceNumber(safeMax / (GRIDLINES - 1), true);
  const max = round6(step * (GRIDLINES - 1));
  const ticks = Array.from({ length: GRIDLINES }, (_, i) => round6(i * step));
  return { max, ticks };
};

/** X pixel position of slot `i` out of `n` slots, spread across `innerWidth`. */
export const xForSlot = (i: number, n: number, innerWidth: number): number =>
  n <= 1 ? innerWidth / 2 : (i / (n - 1)) * innerWidth;

/** Y pixel position of `value` against a `[0, max]` scale over `innerHeight` (0 at the bottom). */
export const yForValue = (
  value: number,
  max: number,
  innerHeight: number,
): number => {
  if (max <= 0) return innerHeight;
  const ratio = Math.min(Math.max(value / max, 0), 1);
  return innerHeight - ratio * innerHeight;
};

/** How many x slots to skip between visible tick labels, so a wide chart doesn't crowd its axis. */
export const tickStride = (n: number): number => Math.max(1, Math.ceil(n / 9));

const runsOf = (
  values: (number | null)[],
  innerWidth: number,
  innerHeight: number,
  max: number,
): Point[][] => {
  const n = values.length;
  const runs: Point[][] = [];
  let current: Point[] = [];
  values.forEach((value, i) => {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      if (current.length) runs.push(current);
      current = [];
      return;
    }
    current.push({
      x: xForSlot(i, n, innerWidth),
      y: yForValue(value, max, innerHeight),
    });
  });
  if (current.length) runs.push(current);
  return runs;
};

/**
 * Builds an SVG path `d` for a series line, in local plot coordinates
 * (0,0 = top-left of the inner drawing area — the caller translates by
 * `PADDING.left`/`PADDING.top`). `null` values break the line into separate
 * `M`-started segments instead of interpolating across the gap.
 */
export const linePath = (
  values: (number | null)[],
  innerWidth: number,
  innerHeight: number,
  max: number,
): string => {
  const runs = runsOf(values, innerWidth, innerHeight, max);
  return runs
    .map((run) =>
      run.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x},${p.y}`).join(" "),
    )
    .join(" ");
};

/**
 * Builds an SVG path `d` for the filled area under a series line, in the
 * same local coordinates as `linePath`. Each contiguous non-null run closes
 * into its own polygon down to the baseline, so gaps never fill across them.
 */
export const areaPath = (
  values: (number | null)[],
  innerWidth: number,
  innerHeight: number,
  max: number,
): string => {
  const runs = runsOf(values, innerWidth, innerHeight, max);
  return runs
    .filter((run) => run.length >= 2)
    .map((run) => {
      const first = run[0]!;
      const last = run[run.length - 1]!;
      const top = run.map((p) => `${p.x},${p.y}`).join(" L ");
      return `M ${first.x},${innerHeight} L ${top} L ${last.x},${innerHeight} Z`;
    })
    .join(" ");
};

/**
 * Running total for the "Acumulado" mode. A `null` slot has no data point
 * (e.g. no publication on that date) and stays `null` — the total simply
 * doesn't advance on that slot — instead of silently coercing to 0.
 */
export const accumulate = (values: (number | null)[]): (number | null)[] => {
  let sum = 0;
  return values.map((value) => {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return null;
    }
    sum += value;
    return sum;
  });
};
